import { Dialog } from "@mui/material";
import classNames from "classnames";
import { t } from "i18next";
import { ReactNode } from "react";
import { CloseIcon } from "../../../icons";
import { IconButtonComponent, PrimaryButtonComponent, TertiaryButtonComponent } from "../buttons/button.module";
import { TypographyComponent } from "../typography/typography.component";
import { TypographyStyles } from "../typography/typography.constants";
import styles from "./modal.styles.module.scss";

type ModalProps = {
  isOpen?: boolean
  isConfirmLoading?: boolean;
  children: ReactNode;
  rootClassName?: string;
  size: "extra-small" | "small" | "medium" | "large" | "extra-large";
  title: string;
  cancelLabel?: string;
  confirmLabel?: string;
  onCancel?: () => void;
  onConfirm?: () => void;
};

const TITLE_HEADER_STYLING: Record<ModalProps["size"], Extract<TypographyStyles, `title-header-${string}`>> = {
  ["extra-small"] : "title-header-sm",
  ["small"] : "title-header-sm",
  ["medium"] : "title-header-sm",
  ["large"] : "title-header-default",
  ["extra-large"] : "title-header-lg",
};

export const ModalComponent = ({
  title,
  /**
  * @arg cancelLabel defaults to "Cancel" but can be set to "" in props to hide the button
  */
  cancelLabel = "Cancel",
  confirmLabel = "Save",
  size = "large",
  isOpen = false,
  isConfirmLoading = false,
  onCancel = () => {},
  onConfirm = () => {},
  rootClassName = "",
  children,
}: ModalProps) => {
  return (
    <Dialog
      open={isOpen}
      onClose={() => !isConfirmLoading && onCancel()}
      maxWidth={false}
      classes={{ root: classNames(styles.root, styles[size]), paper: classNames(styles.paper, rootClassName) }}
      onClick={(e) => e.stopPropagation()}
      slotProps={{
        backdrop: {
          onClick: (e) => e.stopPropagation()
        }
      }}
    >
      <div className={styles.header}>
        <TypographyComponent element="h5" boldness="semi" rootClassName={styles.title} styling={TITLE_HEADER_STYLING[size]}>
          {title}
        </TypographyComponent>
        <IconButtonComponent ariaLabel={t("Close")} onClick={() => !isConfirmLoading && onCancel()} size="small">
          <CloseIcon />
        </IconButtonComponent>
      </div>
      <div className={styles.content}>
        {children}
      </div>
      <div className={styles.footer}>
        {cancelLabel !== "" && (
          <TertiaryButtonComponent ariaLabel={t("Cancel")} onClick={() => !isConfirmLoading && onCancel()} theme="light">
            {cancelLabel}
          </TertiaryButtonComponent>
        )}
        <PrimaryButtonComponent
          onClick={onConfirm}
          theme={"light"}
          isLoading={isConfirmLoading}
        >
          {confirmLabel}
        </PrimaryButtonComponent>
      </div>
    </Dialog>
  );
};
